// PairInvitePage — `/pair/invite` (W2 §4.3 step 2 Sub-flow A).
//
// Per W2: page 1 collects couple-name + relationship_start (defaults to
// "今天" / empty), then on submit a single POST `/api/v1/pairs/invite`
// creates the invite.  We split into two forms: a meta form (name + date)
// + a result panel that shows the BigCodeDisplay + QRCodeView + share.
//
// Bottom actions use `PageBottomActionBar` (per mockup §3 — replaces
// banned Material FAB).  Pre-invite: 「建立邀請連結」 primary.
// Post-invite: 「分享」 primary + 「複製連結」 secondary.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Send, Share2 } from "lucide-react";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { PageHero } from "@/components/primitives/PageHero";
import { PageBottomActionBar } from "@/components/system/PageBottomActionBar";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { ApiError, createInvite, type PairInviteResponse } from "@/lib/api/pair";
import { pairErrorMessage, PAIR_I18N } from "@/lib/i18n/pair";
import { getPublicUrl } from "@/lib/config";
import { isOnOrBeforeToday } from "@/lib/utils/date";
import { BigCodeDisplay } from "@/components/pair/BigCodeDisplay";
import { QRCodeView } from "@/components/pair/QRCodeView";
import { InvitationTimer } from "@/components/pair/InvitationTimer";
import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { useToast } from "@/components/ui/toast";

export function PairInvitePage() {
  const { push: pushToast } = useToast();
  const nav = useNavigate();
  // W35.6: this page docks its action row in BOTH states (pre-invite: 建立
  // 邀請連結; post-invite: 分享 + 複製), so it always owns the bottom edge.
  useHidePillNav();
  const [pairName, setPairName] = useState("我們");
  const [startDate, setStartDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState<PairInviteResponse | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const submit = async () => {
    setNameError(null);
    setDateError(null);
    setFormError(null);
    const trimmed = pairName.trim();
    if (!trimmed || trimmed.length > 50) {
      setNameError("情侶名稱 1-50 字");
      return;
    }
    if (!startDate || !isOnOrBeforeToday(startDate)) {
      setDateError("日期不可晚於今天");
      return;
    }
    setBusy(true);
    try {
      // Per W2 §4.3 + ADR-0013: pass couple-name + start date so the
      // auto-created Pair (W1 onboarding stub) carries the user-confirmed
      // values instead of dropping them on the floor.
      const data = await createInvite({
        couple_name: trimmed,
        relationship_start: startDate,
      });
      setInvite(data);
    } catch (err) {
      if (err instanceof ApiError) setFormError(pairErrorMessage(err.code, err.message));
      else setFormError(PAIR_I18N.invite_failed);
    } finally {
      setBusy(false);
    }
  };

  // Per W35.5 follow-up issue #2: hardcoding `window.location.origin`
  // yields `http://localhost:5173` in dev which is useless when the
  // recipient is on a real device. Read VITE_PUBLIC_URL (e.g.
  // `https://heshe.app` in prod) via the shared helper.
  const inviteUrl = invite ? `${getPublicUrl()}/invite/${invite.invite.code}` : null;

  const handleCopyLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      pushToast({ message: "已複製邀請連結", variant: "success" });
    } catch {
      pushToast({ message: "複製失敗", variant: "danger" });
    }
  };

  const handleShare = async () => {
    if (!inviteUrl || !invite) return;
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: "加入 heshe 配對",
          text: `我的邀請碼是 ${invite.invite.code}`,
          url: inviteUrl,
        });
        return;
      } catch {
        // Fall through to clipboard
      }
    }
    await handleCopyLink();
  };

  return (
    <OnboardingShell current={2} canSkip={false}>
      <PageHero
        eyebrow="建立空間"
        title="邀請你的另一半"
        subtitle="分享連結或 QR, 對方接受後會自動建立 Pair。"
      />

      <FormErrorBanner message={formError} />

      {invite ? (
        <section
          className="mt-8 flex flex-col items-center gap-6 pb-32"
          data-testid="pair-invite-result"
        >
          <BigCodeDisplay code={invite.invite.code} expiresAt={invite.invite.expires_at} />
          <InvitationTimer expiresAt={invite.invite.expires_at} onExpire={() => setInvite(null)} />
          {/* W40.6: the QR encodes `inviteUrl` — the same string shown below it
              and handed to the share sheet — rather than the bare code. One
              value, so the square and the link cannot disagree. */}
          <QRCodeView value={inviteUrl} title={`邀請 QR 碼，掃描後開啟 ${inviteUrl}`} />
          <p className="text-body-sm text-ink-3">
            連結:{" "}
            <code className="text-body-sm" data-testid="pair-invite-link">
              {inviteUrl}
            </code>
          </p>
          <PageBottomActionBar
            primary={{
              label: "分享邀請",
              onClick: handleShare,
              testId: "pair-invite-share",
              leadingIcon: <Share2 size={18} aria-hidden />,
            }}
            // W35.6: leaving the page is one tap from the action, matching
            // every other create / edit page.
            back={{
              label: "返回配對",
              onClick: () => nav("/pair"),
              testId: "pair-invite-back",
            }}
            secondary={[
              {
                icon: Copy,
                label: "複製連結",
                onClick: handleCopyLink,
                testId: "pair-invite-copy",
              },
            ]}
            testId="pair-invite-result-bar"
          />
        </section>
      ) : (
        <div className="mt-8 flex flex-col gap-4 pb-32" data-testid="pair-invite-form">
          <FormField
            name="pair_name"
            label={PAIR_I18N.label_couple_name}
            required
            minLength={1}
            maxLength={50}
            value={pairName}
            onChange={setPairName}
            invalid={Boolean(nameError)}
            errorMessage={nameError ?? undefined}
            disabled={busy}
            data-testid="pair-name-input"
            hint="預設為 「我們」; 之後隨時可在 Settings 改名。"
          />
          <FormField
            name="relationship_start"
            label={PAIR_I18N.label_relationship_start}
            type="date"
            required
            max={todayISO()}
            value={startDate}
            onChange={setStartDate}
            invalid={Boolean(dateError)}
            errorMessage={dateError ?? undefined}
            disabled={busy}
            data-testid="pair-start-input"
          />
          <PageBottomActionBar
            primary={{
              label: busy ? "建立中…" : "建立邀請連結",
              onClick: () => void submit(),
              loading: busy,
              testId: "pair-invite-submit",
              leadingIcon: <Send size={18} aria-hidden />,
            }}
            back={{
              label: "返回配對",
              onClick: () => nav("/pair"),
              testId: "pair-invite-back",
            }}
            testId="pair-invite-form-bar"
          />
        </div>
      )}
    </OnboardingShell>
  );
}

function todayISO(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}
